import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, MessagesSquare } from 'lucide-react';
import { api } from '../api';
import { ChatThread, ChatComposer } from './ChatModule';

export default function AdminChatPanel({ agent, onClose, onRead }) {
  const [messages, setMessages] = useState([]);

  const append = useCallback((msg) => {
    setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
  }, []);

  useEffect(() => {
    api.get(`/chat/${agent.id}`).then((r) => setMessages(r.data));
    api.post(`/chat/${agent.id}/read?side=admin`).then(() => onRead && onRead());
    const handler = (e) => {
      if (e.detail.agent_id === agent.id) {
        append(e.detail.message);
        if (e.detail.message.sender === 'agent') {
          api.post(`/chat/${agent.id}/read?side=admin`).then(() => onRead && onRead());
        }
      }
    };
    window.addEventListener('snd-chat-admin', handler);
    return () => window.removeEventListener('snd-chat-admin', handler);
  }, [agent.id, append, onRead]);

  const send = async (text, attachment) => {
    const r = await api.post(`/chat/${agent.id}`, {
      sender: 'admin', sender_name: 'Admin', text, attachment: attachment || null,
    });
    append(r.data);
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="admin-chat-overlay">
      <div
        className="bg-[#FBF7FF] border-2 border-purple-300 rounded-lg sticky-shadow w-full max-w-md flex flex-col max-h-[85vh] relative rotate-[0.3deg]"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-chat-panel"
      >
        <div className="tape" />
        <div className="flex items-center justify-between px-4 pt-4 pb-2.5 border-b border-dashed border-purple-300">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center font-display font-extrabold text-xs text-slate-800 border-2 border-white shadow" style={{ background: agent.avatar_color }}>
              {agent.name.split(' ').map((w) => w[0]).join('')}
            </div>
            <div>
              <h3 className="font-display font-bold text-base text-slate-900 leading-none flex items-center gap-1.5"><MessagesSquare size={14} className="text-purple-600" /> {agent.name}</h3>
              <p className={`text-[10px] font-semibold ${agent.online ? 'text-emerald-600' : 'text-slate-400'}`}>{agent.online ? '● Online' : '○ Offline — will see it on next login'}</p>
            </div>
          </div>
          <button data-testid="admin-chat-close" onClick={onClose} className="p-1.5 rounded-full hover:bg-purple-100 text-slate-500"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-y-auto scroll-thin p-3 min-h-[220px]">
          {messages.length === 0
            ? <p className="font-hand text-xl text-purple-800/50 text-center py-10">Say hello — a sticky note will pop up on {agent.name.split(' ')[0]}'s board! 👋</p>
            : <ChatThread messages={messages} mySide="admin" accent="bg-slate-900" />}
        </div>
        <div className="p-3 border-t border-dashed border-purple-300">
          <ChatComposer onSend={send} accentBtn="bg-slate-900 hover:bg-slate-700" idPrefix="admin-chat" />
        </div>
      </div>
    </div>,
    document.body
  );
}
